<template>
  <div class="languages-configuration" data-testid="languages-configuration">
    <!-- Toolbar: breadcrumb back to the Configuration grid. -->
    <div class="languages-configuration__toolbar">
      <ExBreadcrumbs
        class="languages-configuration__breadcrumbs"
        :breadcrumbsList="breadcrumbItems"
      />
    </div>

    <p
      v-if="errorMessage"
      class="languages-configuration__error"
      role="alert"
      data-testid="languages-error"
    >
      {{ errorMessage }}
    </p>

    <AccountLanguagesTable
      :languages="pagedLanguages"
      :loading="loading"
      :filters="filters"
      :applied-filter-chips="appliedFilterChips"
      :pagination="pagination"
      @filter-change="onFilterChange"
      @more-filters="showFilterModal = true"
      @remove-filter="onRemoveFilter"
      @add-language="openAddDialog"
      @page-change="onPageChange"
      @row-action="onRowAction"
    />

    <FilterLanguagesModal
      v-model="showFilterModal"
      :initial-statuses="filters.status"
      @apply="onFiltersApplied"
    />

    <AddOperatingLanguageDialog
      :value="showAddDialog"
      :languages="addableLanguages"
      :dubbing-supported-languages="dubbingSupportedLanguages"
      :loading="adding"
      :catalog-loading="catalogLoading"
      :error-code="addErrorCode"
      @input="showAddDialog = $event"
      @close="onDialogClosed"
      @onAdd="onAddLanguage"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import AccountLanguagesTable from "@components/AccountLanguagesTable/AccountLanguagesTable.vue";
import FilterLanguagesModal from "@components/FilterLanguagesModal/FilterLanguagesModal.vue";
import {
  STATUS_FILTER_OPTIONS,
  languageCode,
  normalizeStatus,
} from "@components/AccountLanguagesTable/accountLanguagesTableRow.js";
import AddOperatingLanguageDialog from "@components/AddOperatingLanguageDialog/AddOperatingLanguageDialog.vue";
import ExBreadcrumbs from "@components/breadcrumbs/ExBreadcrumbs.vue";
import {
  addOperatingLanguage,
  getOperatingLanguages,
  getSupportedLanguageCatalog,
  reactivateOperatingLanguage,
  retireOperatingLanguage,
} from "@/api";

const props = defineProps({
  /**
   * Account id to load operating languages for. Falls back to the
   * `currentAccountID` localStorage value used by the rest of the Settings flow.
   */
  accountId: {
    type: [Number, String],
    default: null,
  },
  /** Dubbing-capable codes/ids forwarded verbatim to the add dialog. */
  dubbingSupportedLanguages: {
    type: Array,
    default: () => [],
  },
});

const breadcrumbItems = [
  { text: "Configuration", href: "/settings/configuration", disabled: false },
  { text: "Languages", disabled: true },
];

const languages = ref([]);
const loading = ref(false);
const errorMessage = ref("");
const PAGE_SIZE = 8;
const ALL_STATUSES = STATUS_FILTER_OPTIONS.map((option) => option.value);

// Both statuses selected is the default (both chips shown); an empty selection means no status filter.
const filters = ref({ status: [...ALL_STATUSES], search: "" });
const page = ref(1);
const showFilterModal = ref(false);

const showAddDialog = ref(false);
const adding = ref(false);
const addErrorCode = ref("");
const catalog = ref([]);
const catalogLoading = ref(false);

const resolvedAccountId = computed(() => {
  if (props.accountId !== null && props.accountId !== undefined && props.accountId !== "") {
    return props.accountId;
  }
  try {
    return JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return null;
  }
});

const filteredLanguages = computed(() => {
  const query = String(filters.value.search || "")
    .trim()
    .toLowerCase();
  return languages.value.filter((language) => {
    const statuses = filters.value.status;
    if (statuses.length && !statuses.includes(normalizeStatus(language))) {
      return false;
    }
    if (query) {
      const name = String(language.name || "").toLowerCase();
      const code = String(language.locale_code || language.code || "").toLowerCase();
      if (!name.includes(query) && !code.includes(query)) {
        return false;
      }
    }
    return true;
  });
});

const pageCount = computed(() =>
  Math.max(1, Math.ceil(filteredLanguages.value.length / PAGE_SIZE))
);

const currentPage = computed(() => Math.min(Math.max(page.value, 1), pageCount.value));

const pagedLanguages = computed(() => {
  const start = (currentPage.value - 1) * PAGE_SIZE;
  return filteredLanguages.value.slice(start, start + PAGE_SIZE);
});

const pagination = computed(() => ({ page: currentPage.value, pageCount: pageCount.value }));

const appliedFilterChips = computed(() =>
  STATUS_FILTER_OPTIONS.filter((option) => filters.value.status.includes(option.value)).map(
    (option) => option.label
  )
);

// Picker options: active catalog rows (never English) not already configured, active or retired.
const addableLanguages = computed(() => {
  const configuredCodes = new Set(
    languages.value.map((language) => languageCode(language).toLowerCase())
  );
  return catalog.value
    .filter((entry) => {
      const code = String(entry?.locale_code || "").toLowerCase();
      return code && code !== "en" && entry.active !== false && !configuredCodes.has(code);
    })
    .map((entry) => ({
      ...entry,
      id: entry.locale_code,
      code: entry.locale_code,
      name: entry.name || entry.native_name || entry.locale_code,
    }));
});

async function fetchLanguages() {
  const accountId = resolvedAccountId.value;
  if (accountId === null || accountId === undefined) {
    errorMessage.value = "Could not determine the account to load languages for.";
    return;
  }

  loading.value = true;
  errorMessage.value = "";
  try {
    const { data } = await getOperatingLanguages(accountId);
    languages.value = Array.isArray(data) ? data : data?.results || [];
  } catch {
    languages.value = [];
    errorMessage.value = "Failed to load operating languages. Please try again.";
  } finally {
    loading.value = false;
  }
}

function onFilterChange(nextFilters) {
  filters.value = { ...filters.value, ...nextFilters };
  page.value = 1;
}

function onFiltersApplied({ status }) {
  onFilterChange({ status });
}

function onRemoveFilter(chipLabel) {
  const removed = STATUS_FILTER_OPTIONS.find((option) => option.label === chipLabel);
  if (!removed) {
    return;
  }
  onFilterChange({ status: filters.value.status.filter((status) => status !== removed.value) });
}

function onPageChange(nextPage) {
  page.value = Number(nextPage) || 1;
}

async function onRowAction({ id, action }) {
  const accountId = resolvedAccountId.value;
  if (accountId === null || accountId === undefined || id == null) {
    return;
  }

  errorMessage.value = "";
  try {
    if (action === "retire") {
      await retireOperatingLanguage(accountId, id);
    } else if (action === "reactivate") {
      await reactivateOperatingLanguage(accountId, id);
    } else {
      // "setAsDefault" is a pre-existing ER-11416 menu item with no backend
      // support in the canonical operating-languages spec — intentionally no-op.
      return;
    }
    await fetchLanguages();
  } catch {
    errorMessage.value = "Failed to update the language. Please try again.";
  }
}

async function fetchCatalog() {
  const accountId = resolvedAccountId.value;
  if (accountId === null || accountId === undefined) {
    return;
  }

  catalogLoading.value = true;
  try {
    const { data } = await getSupportedLanguageCatalog(accountId);
    catalog.value = Array.isArray(data) ? data : data?.results || [];
  } catch {
    catalog.value = [];
    errorMessage.value = "Failed to load languages. Please try again.";
  } finally {
    catalogLoading.value = false;
  }
}

function openAddDialog() {
  addErrorCode.value = "";
  errorMessage.value = "";
  showAddDialog.value = true;
  return fetchCatalog();
}

function onDialogClosed() {
  addErrorCode.value = "";
}

async function onAddLanguage(selectedLanguage) {
  const accountId = resolvedAccountId.value;
  if (accountId === null || accountId === undefined || !selectedLanguage) {
    return;
  }

  const localeCode = selectedLanguage.locale_code || selectedLanguage.code || selectedLanguage.id;

  adding.value = true;
  addErrorCode.value = "";
  errorMessage.value = "";
  try {
    await addOperatingLanguage(accountId, { locale_code: localeCode });
    showAddDialog.value = false;
    await fetchLanguages();
  } catch (error) {
    addErrorCode.value = error?.response?.data?.code || "";
  } finally {
    adding.value = false;
  }
}

onMounted(fetchLanguages);
</script>

<style scoped>
.languages-configuration {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.languages-configuration__toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
}

.languages-configuration__breadcrumbs {
  width: auto;
}

.languages-configuration__error {
  margin: 0;
  padding: 12px 16px;
  border: 1px solid #fecaca;
  border-radius: 8px;
  background: #fef2f2;
  color: #b42318;
  font-size: 14px;
  line-height: 20px;
}
</style>
